/* eslint-disable max-lines */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUpdateEffect } from 'ahooks';
import { Dialog, Swiper } from 'antd-mobile';
import { Skeleton } from 'antd';
import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import Refresh from '@/assets/icon/Refresh.svg';
import noDataImg from '@/assets/image/no-data.png';
import { ScrollView } from '@/components';
import service from '@/service';
import { getPadVersion, isNewSoftwareVersion, Toast } from '@/utils';
import { OneMoreOrderParams } from '@/pages/user/service-detail';
import noMachinImg from '@/assets/image/no-machine.png';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { isShowMachineLockState } from '@/pages/device/device-management';
import cx from './index.module.scss';
import FilterHeader, { FilterDimension } from './components/FilterHeader';
import { therapyBodyPart, therapyType } from './constant';
import PlanCards from './components/PlanCards';
import MachineCards from './components/MachineCards';
import { selectedApkVersion } from '@/store/device';
import { useSelector } from 'react-redux';

interface Props {
  className?: string;
  scrollViewRef: React.MutableRefObject<ScrollViewRefAttrs>;
  onChangePlan: (planItem: GetAllProductsResponse) => void;
  onSelectMachine: (deviceItem: DeviceItem) => void;
  getAllPlanRef: React.MutableRefObject<VoidFunc>;
  setSelectedInfoByUser?: React.Dispatch<
    React.SetStateAction<{
      bodyPart: Service.BodyPart;
      planType: Massage.MassageItem;
    }>
  >;
}

const SINGLE_PAGE_LENGTH = 6;

export interface ClassifyPlanListType {
  byBodyPart: Map<Service.BodyPart, GetAllProductsResponse[]>;
  byPlanType: Map<Massage.MassageItem, GetAllProductsResponse[]>;
}

const SelectPlan: React.FC<Props> = ({
  className,
  onChangePlan,
  onSelectMachine,
  scrollViewRef,
  getAllPlanRef,
  setSelectedInfoByUser,
}) => {
  const apkVersion = useSelector(selectedApkVersion);
  const currentSwiperIndex = useRef(0);
  const [selectMachineIndex, setSelectMachineIndex] = useState(-1);
  const [allSchemes, setAllSchemes] = useState<GetAllProductsResponse[]>([]);
  const [hasLoadedMachine, setHasLoadedMachine] = useState(false);
  const [isGettingProducts, setIsGettingProducts] = useState(true);
  const [machineList, setMachineList] = useState<DeviceItem[]>([]);
  const [deviceListOriginal, setDeviceListOriginal] = useState<DeviceItem[]>([]);
  const [isLoadingMachine, setisLoadingMachine] = useState(true);
  const [tooltipPartOpen, setTooltipPartOpen] = useState<Record<number, boolean>>({});
  const swiperRef = useRef(null);
  const scrollViewRef2 = useRef<ScrollViewRefAttrs>(null);
  const { state } = useLocation() as {
    state: OneMoreOrderParams & {
      selectPlanItem: GetAllProductsResponse;
      selectMachineItem: DeviceItem;
    };
  };

  const [therapyPlanOpen, setTooltipTherapyPlanOpen] = useState<Record<number, boolean>>({});

  const [selectPlanItem, setSelectPlanItem] = useState<GetAllProductsResponse>(null);

  const [currentFilterDimension, setCurrentFilterDimension] = useState<FilterDimension>(null);

  /** 通过理疗部位和类型进行方案分类 */
  const classifyPlanList: ClassifyPlanListType = useMemo(() => {
    const classifyPlanListByBodyPart = new Map<Service.BodyPart, GetAllProductsResponse[]>(null);
    const classifyPlanListByPlanType = new Map<Massage.MassageItem, GetAllProductsResponse[]>(null);

    allSchemes?.forEach(item => {
      if (classifyPlanListByBodyPart.get(item.bodyPart)) {
        const currentItems = classifyPlanListByBodyPart.get(item.bodyPart);
        currentItems?.splice(currentItems?.length, 0, item);

        classifyPlanListByBodyPart.set(item.bodyPart, currentItems);
      } else {
        classifyPlanListByBodyPart.set(item.bodyPart, [item]);
      }

      if (classifyPlanListByPlanType.get(item.items?.[0])) {
        const currentItems = classifyPlanListByPlanType.get(item.items?.[0]);
        currentItems?.splice(currentItems?.length, 0, item);

        classifyPlanListByPlanType.set(item.items?.[0], currentItems);
      } else {
        classifyPlanListByPlanType.set(item.items?.[0], [item]);
      }
    });
    return {
      byBodyPart: classifyPlanListByBodyPart,
      byPlanType: classifyPlanListByPlanType,
    };
  }, [allSchemes]);

  // 通过部位排序
  const sortByBodyPart = useCallback((a: GetAllProductsResponse, b: GetAllProductsResponse) => {
    return therapyBodyPart.indexOf(a?.bodyPart) - therapyBodyPart.indexOf(b?.bodyPart);
  }, []);

  // 通过理疗类型排序
  const sortByPlanType = useCallback((a: GetAllProductsResponse, b: GetAllProductsResponse) => {
    if (b?.items?.length > a?.items?.length) {
      return 1;
    } else if (b?.items?.length < a?.items?.length) {
      return -1;
    } else {
      // 只有一种理疗类型
      return therapyType.indexOf(a?.items?.[0]) - therapyType.indexOf(b?.items?.[0]);
    }
  }, []);

  const getAllProducts = useCallback(async () => {
    try {
      setIsGettingProducts(true);
      const { data } = await service.service.getAllProducts();
      const deviceInfo = await Device.getInfo();

      let appVersion = getPadVersion(apkVersion, deviceInfo?.platform);
      if (deviceInfo.platform === 'android') {
        const appInfo = await App.getInfo();
        appVersion = appInfo?.version;
      }

      const filteredData = data?.filter(item => {
        const versions = item?.deviceVersions || [];
        if (isNewSoftwareVersion(appVersion, '2.0.0')) {
          return (
            (versions?.length === 0 || versions?.includes('1.3') || versions?.includes('1.4')) &&
            !item?.items?.includes('Moxibustion')
          );
        } else {
          return versions?.length === 0 || versions?.includes('1.2');
        }
      });

      setAllSchemes(filteredData);
      // 再来一单时，默认选择该订单方案
      if (state?.storeGoodsId) {
        setSelectPlanItem(data?.find(item => item.storeGoodsId === state?.storeGoodsId));
      }
      // 免责声明兜底策略 返回页面时候，需要对已选设备和方案进行勾选
      else if (state?.selectPlanItem) {
        setSelectPlanItem(state?.selectPlanItem);
      }
    } finally {
      setIsGettingProducts(false);
    }
  }, [apkVersion, state?.selectPlanItem, state?.storeGoodsId]);

  const getMachines = useCallback(async () => {
    setisLoadingMachine(true);
    try {
      const { data } = await service.device.getDeviceList();
      setDeviceListOriginal(data);

      const deviceInfo = await Device.getInfo();
      let appVersion = getPadVersion(apkVersion, deviceInfo?.platform);
      if (deviceInfo.platform === 'android') {
        const appInfo = await App.getInfo();
        appVersion = appInfo?.version;
      }

      // 根据机器状态排序
      const allStateMachine: Record<DeviceItem['state'] | 'NotUse', DeviceItem[]> = {
        NotUse: [], // 初始化 NotUse 为空数组
      } as any;
      data
        ?.filter(item => !isShowMachineLockState(item))
        .forEach(item => {
          // 判断是否为「本Pad不可操作」（NotUse） APP版本大于等于2.0.0时，判断软件版本是否小于2.0.0，否则为「本Pad不可操作」
          const isNotUse = isNewSoftwareVersion(appVersion, '2.0.0')
            ? !isNewSoftwareVersion(item?.softwareVersion, '2.0.0')
            : isNewSoftwareVersion(item?.softwareVersion, '2.0.0');

          if (isNotUse) {
            const notUseItem = { ...item, state: 'NotUse' } as DeviceItem;
            allStateMachine['NotUse'] = [...(allStateMachine['NotUse'] || []), notUseItem];
          } else {
            allStateMachine[item.state] = allStateMachine[item.state] ? [...allStateMachine[item.state], item] : [item];
          }
        });

      const lockedMachineList = data?.filter(item => isShowMachineLockState(item));

      const sortedMachine: DeviceItem[] = [
        ...(allStateMachine['Idle'] || []),
        ...(allStateMachine['ManualOnly'] || []),
        ...(allStateMachine['Busy'] || []),
        ...(allStateMachine['Abnormal'] || []),
        ...(lockedMachineList || []),
        ...(allStateMachine['NotUse'] || []),
        ...(allStateMachine['Offline'] || []),
      ];
      // setSelectMachineIndex(-1);
      // 免责声明兜底策略 返回页面时候，需要对已选设备和方案进行勾选
      if (state?.selectMachineItem) {
        setSelectMachineIndex(sortedMachine?.findIndex(item => item.deviceId === state?.selectMachineItem?.deviceId));
      } else {
        setSelectMachineIndex(-1);
      }
      setMachineList(sortedMachine);
    } catch (e) {
      console.log(e);
    } finally {
      scrollViewRef.current?.handleRefresh();
      scrollViewRef2.current?.handleRefresh();
      setHasLoadedMachine(true);
      setisLoadingMachine(false);
    }
  }, [apkVersion, scrollViewRef, state?.selectMachineItem]);

  const handleClickMachineItem = useCallback(
    (item: DeviceItem, index: number) => () => {
      if (item.state === 'NotUse') {
        const deviceState = deviceListOriginal?.find(itm => itm.deviceId === item?.deviceId)?.state;
        if (['Idle']?.includes(deviceState)) {
          Toast.show('本 Pad 不可操作', { duration: 2000 });
        }
        return;
      }
      // 已锁机的机器无法选中
      if (isShowMachineLockState(item)) {
        Dialog.alert({
          title: '设备已锁机',
          content: (
            <div>
              <div>该设备已被代理商远程锁机，</div>
              <div>请联系代理商恢复使用</div>
              {!!item?.contactInfo && (
                <div style={{ color: '#0055FF' }}>
                  联系方式：
                  <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{item?.contactInfo}</span>
                </div>
              )}
            </div>
          ),
          confirmText: '我知道了',
          bodyClassName: cx('software-not-support'),
        });
        return;
      }

      if (selectPlanItem?.onlyManual && item.state === 'ManualOnly') {
        setSelectMachineIndex(index);
      }
      if (item.state === 'Idle') {
        setSelectMachineIndex(index);
      }
    },
    [selectPlanItem]
  );

  const handleClickPlanCard = useCallback((item: GetAllProductsResponse) => {
    setSelectPlanItem(item);
  }, []);

  // 当前筛选的方案列表
  const currentPlanListByFilter = useMemo(() => {
    if (!(currentFilterDimension?.bodyPart || currentFilterDimension?.planType)) {
      return allSchemes?.sort(sortByBodyPart)?.sort(sortByPlanType);
    }

    // 当前选择了理疗部位
    if (currentFilterDimension?.bodyPart) {
      let currentPlanListsByBodyPart = [
        ...(classifyPlanList?.byBodyPart.get(currentFilterDimension?.bodyPart) || []),
        // 同时筛选出人工方案，其部位表示为All
        ...(classifyPlanList?.byBodyPart.get('All') || []),
      ];

      // 选择腹部时，过滤磁震波方案
      if (currentFilterDimension?.bodyPart === 'Abdomen') {
        currentPlanListsByBodyPart = currentPlanListsByBodyPart.filter(item => item?.items?.[0] !== 'Shockwave');
      }

      // 同时选择了理疗类型
      if (currentFilterDimension?.planType) {
        const filteredPlans = currentPlanListsByBodyPart?.filter(
          item => item?.items?.[0] === currentFilterDimension.planType
        );
        return filteredPlans;
      } else {
        return currentPlanListsByBodyPart?.sort(sortByPlanType);
      }
    } else if (currentFilterDimension?.planType) {
      return classifyPlanList?.byPlanType.get(currentFilterDimension?.planType)?.sort(sortByBodyPart);
    }
  }, [allSchemes, classifyPlanList, currentFilterDimension, sortByPlanType, sortByBodyPart]);

  // 序列化为swiper展示数据
  const formatSwiperData = useMemo(() => {
    const showData = state?.storeGoodsId
      ? // 再来一单时，只展示之前订单的方案，且隐藏草稿单
        allSchemes?.filter(item => item.storeGoodsId === state.storeGoodsId && !item.isDraft)
      : currentPlanListByFilter;

    const newArr = [];
    for (let i = 0; i < showData?.length || 0; i += SINGLE_PAGE_LENGTH) {
      newArr.push((showData || []).slice(i, i + SINGLE_PAGE_LENGTH));
    }
    return newArr;
  }, [allSchemes, currentPlanListByFilter, state]);

  useUpdateEffect(() => {
    onSelectMachine(machineList[selectMachineIndex]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectMachineIndex]);

  useEffect(() => {
    const currentMachine = machineList[selectMachineIndex];
    onChangePlan(selectPlanItem);

    // 如果当前选择的机器是仅支持人工，此时选择的不是仅人工的方案时，取消当前勾选的机器
    if (currentMachine?.state === 'ManualOnly' && !selectPlanItem?.onlyManual) {
      setSelectMachineIndex(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineList, selectMachineIndex, selectPlanItem]);

  useEffect(() => {
    getAllPlanRef.current = getAllProducts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAllProducts]);

  useEffect(() => {
    getAllProducts();
    getMachines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={cx(className, 'select-plan')}
      onTouchStart={() => {
        Object.keys(tooltipPartOpen)?.length && setTooltipPartOpen({});
        Object.keys(therapyPlanOpen)?.length && setTooltipTherapyPlanOpen({});
      }}
    >
      <div className={cx('select-box', 'extra-padding')}>
        <FilterHeader
          onFilter={filterDimension => {
            setSelectedInfoByUser(filterDimension);
            setCurrentFilterDimension(filterDimension);
            scrollViewRef.current?.handleRefresh();
            swiperRef.current?.swipeTo(0);
          }}
          classifyPlanList={classifyPlanList}
          scrollViewRef={scrollViewRef}
          therapyPlanOpen={therapyPlanOpen}
          setTooltipTherapyPlanOpen={setTooltipTherapyPlanOpen}
          tooltipPartOpen={tooltipPartOpen}
          setTooltipPartOpen={setTooltipPartOpen}
          isGettingProducts={isGettingProducts}
        />
        {!isGettingProducts ? (
          <Swiper
            {...(formatSwiperData?.length < 2 ? { indicator: false } : {})}
            ref={swiperRef}
            defaultIndex={0}
            onIndexChange={index => (currentSwiperIndex.current = index)}
            style={{ '--track-padding': '0 0 35px' }}
            className={cx('plan-list', { show: allSchemes?.length > 0 }, { notShowIndicator: allSchemes?.length <= 6 })}
          >
            {formatSwiperData?.map((planList: GetAllProductsResponse[]) => (
              <Swiper.Item key={`${planList?.[0]?.storeGoodsId}${planList?.[0]?.onlyManual}`} className={cx('list')}>
                <PlanCards planList={planList} selectPlanItem={selectPlanItem} handleClick={handleClickPlanCard} />
              </Swiper.Item>
            ))}
          </Swiper>
        ) : (
          <div className={cx['loading']}>
            {[...new Array(6)].map((_, key) => (
              <Skeleton.Image className={cx['item']} active key={key} />
            ))}
          </div>
        )}

        <div className={cx('no-plan', { show: allSchemes?.length === 0 && !isGettingProducts })}>
          <div className={cx('no-plan-content')}>
            <img src={noDataImg} alt="no-data" />
            <div className={cx('no-service-record-text')}>暂无方案</div>
          </div>
        </div>
      </div>
      <div className={cx('select-box')}>
        <div className={cx('title', 'select-machine-title')}>
          <div className={cx('therapy-title')}>
            <div>选择理疗设备</div>
            <div className={cx('refresh')} onClick={getMachines}>
              <div>刷新</div>
              <img src={Refresh} className={cx('icon')} alt="refresh" />
            </div>
          </div>
        </div>
        <Skeleton loading={!hasLoadedMachine} active title={false} paragraph={{ rows: 4, width: '100%' }}>
          <ScrollView
            ref={scrollViewRef2}
            className={cx('select-machine', { show: hasLoadedMachine })}
            scrollX
            scrollY={false}
            // 外层ScorllView已经设置了allowClick为true，这里设置为false防止派发两次click事件
            allowClick={false}
          >
            {isLoadingMachine ? (
              <div className={cx('loading-wrapper')}>
                <Loading3QuartersOutlined className={cx('icon')} spin />
              </div>
            ) : machineList?.length ? (
              <MachineCards
                machineList={machineList}
                deviceListOriginal={deviceListOriginal}
                selectMachineIndex={selectMachineIndex}
                handleClickMachineItem={handleClickMachineItem}
              />
            ) : (
              <div className={cx('no-machine')}>
                <img src={noMachinImg} alt="no-machine" />
                <div className={cx('no-machine-text')}>暂无可用理疗设备</div>
              </div>
            )}
          </ScrollView>
        </Skeleton>
      </div>
    </div>
  );
};

export default SelectPlan;
